import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AppBar,
  Toolbar,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { TRADE } from '../../infrastructure/config/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';

function normalizeBaseSymbol(symbol) {
  return String(symbol || '').trim().toUpperCase().replace(/USDT$/, '');
}

function normalizeSettingSymbol(symbol) {
  const base = normalizeBaseSymbol(symbol);
  return base ? `${base}USDT` : '';
}

function cleanSymbolTradeSettings(settings) {
  return Object.entries(settings || {}).reduce((acc, [symbol, value]) => {
    const key = normalizeSettingSymbol(symbol);
    const investment = Number(value?.investment);
    if (!key || !Number.isFinite(investment) || investment <= 0) {
      return acc;
    }

    acc[key] = {
      investment,
      enabled: value?.enabled !== false,
    };
    return acc;
  }, {});
}

const SymbolManagement = () => {
  const navigate = useNavigate();
  const [symbols, setSymbols] = useState([]);
  const [symbolTradeSettings, setSymbolTradeSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [formData, setFormData] = useState({
    symbol: '',
    is_active: true,
    slot_number: 0,
    webhook_version: 'v18',
    investment: '',
    original_symbol: ''
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsedUser = JSON.parse(userStr);
      setUser(parsedUser);
    }
    setIsCheckingAuth(false);
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchSymbols();
    }
  }, [user]);

  const fetchSymbols = async () => {
    setIsLoading(true);
    try {
      // Lấy tất cả symbols (cả active và inactive) để admin quản lý
      const [slotsResponse, settingsResponse] = await Promise.all([
        api.get('/api/managed-webhook-slots'),
        api.get(TRADE.V18_SYMBOL_TRADE_SETTINGS),
      ]);
      setSymbols(slotsResponse.data.data);
      setSymbolTradeSettings(cleanSymbolTradeSettings(settingsResponse.data?.data || {}));
      enqueueSnackbar('Đã tải danh sách symbols', { variant: 'success' });
    } catch (error) {
      console.error('Error fetching symbols:', error);
      enqueueSnackbar(error.response?.data?.message || error.response?.data?.error || 'Lỗi khi tải symbols', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (symbol = null) => {
    if (symbol) {
      // Edit mode
      setEditingSymbol(symbol);
      const symbolKey = normalizeSettingSymbol(symbol.symbol);
      setFormData({
        symbol: symbol.symbol,
        is_active: symbol.is_active,
        slot_number: symbol.slot_number,
        webhook_version: symbol.webhook_version || 'v18',
        investment: symbolTradeSettings[symbolKey]?.investment || '',
        original_symbol: symbol.symbol
      });
    } else {
      enqueueSnackbar('20 slot webhook là cố định, chỉ hỗ trợ chỉnh sửa slot hiện có', { variant: 'info' });
      return;
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSymbol(null);
    setFormData({ symbol: '', is_active: true, slot_number: 0, webhook_version: 'v18', investment: '', original_symbol: '' });
  };

  const handleSave = async () => {
    try {
      if (!formData.symbol.trim()) {
        enqueueSnackbar('Vui lòng nhập tên symbol', { variant: 'warning' });
        return;
      }

      if (editingSymbol) {
        await api.put(`/api/managed-webhook-slots/${editingSymbol.id}`, {
          ...formData,
          slot_number: editingSymbol.slot_number,
        });

        const originalKey = normalizeSettingSymbol(editingSymbol.symbol);
        const nextKey = normalizeSettingSymbol(formData.symbol);
        const nextSettings = { ...symbolTradeSettings };
        delete nextSettings[originalKey];

        const investment = Number(formData.investment);
        if (nextKey && Number.isFinite(investment) && investment > 0) {
          nextSettings[nextKey] = {
            investment,
            enabled: true,
          };
        }

        const settingsResponse = await api.put(TRADE.V18_SYMBOL_TRADE_SETTINGS, nextSettings);
        setSymbolTradeSettings(cleanSymbolTradeSettings(settingsResponse.data?.data || nextSettings));
        enqueueSnackbar('Đã cập nhật symbol thành công', { variant: 'success' });
      }

      handleCloseDialog();
      fetchSymbols();
    } catch (error) {
      console.error('Error saving symbol:', error);
      enqueueSnackbar(error.response?.data?.message || error.response?.data?.error || 'Lỗi khi lưu symbol', { variant: 'error' });
    }
  };

  const handleToggleActive = async (symbol) => {
    try {
      await api.put(`/api/managed-webhook-slots/${symbol.id}`, {
        is_active: !symbol.is_active,
        slot_number: symbol.slot_number,
      });
      enqueueSnackbar(
        symbol.is_active ? 'Đã vô hiệu hóa symbol' : 'Đã kích hoạt symbol',
        { variant: 'success' }
      );
      fetchSymbols();
    } catch (error) {
      console.error('Error toggling symbol:', error);
      enqueueSnackbar(error.response?.data?.error || 'Lỗi khi thay đổi trạng thái', { variant: 'error' });
    }
  };

  const handleDelete = async (symbol) => {
    if (!window.confirm(`Bạn có chắc muốn xóa symbol ${symbol.symbol}?`)) {
      return;
    }

    try {
      await api.put(`/api/managed-webhook-slots/${symbol.id}`, {
        is_active: false,
        slot_number: symbol.slot_number,
      });
      enqueueSnackbar('Đã vô hiệu hóa symbol thành công', { variant: 'success' });
      fetchSymbols();
    } catch (error) {
      console.error('Error deleting symbol:', error);
      enqueueSnackbar(error.response?.data?.error || 'Lỗi khi xóa symbol', { variant: 'error' });
    }
  };

  if (isCheckingAuth) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (!user || user.role !== 'admin') {
    enqueueSnackbar('Bạn không có quyền truy cập trang này.', { variant: 'error' });
    return <Navigate to="/dashboard" />;
  }

  return (
    <React.Fragment>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }}>
            Quản Lý Symbols
          </Typography>
          <Tooltip title="Refresh dữ liệu">
            <IconButton color="inherit" onClick={fetchSymbols} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>💡 Hướng dẫn:</strong> Thêm/xóa symbols ở đây, TradingView sẽ tự động cập nhật.
            Không cần deploy lại code!
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tổng số symbols: <strong>{symbols.length}</strong>
            {' | '}
            Đang hoạt động: <strong>{symbols.filter(s => s.is_active).length}</strong>
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>STT</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Webhook Version</TableCell>
                    <TableCell align="right">Investment USDT</TableCell>
                    <TableCell>Trạng Thái</TableCell>
                    <TableCell>Thứ Tự Hiển Thị</TableCell>
                    <TableCell align="right">Hành Động</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {symbols.map((symbol, index) => (
                    <TableRow key={symbol.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <strong>{symbol.symbol}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(symbol.webhook_version || 'v18').toUpperCase()}
                          color={(symbol.webhook_version || 'v18') === 'v19' ? 'secondary' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {symbolTradeSettings[normalizeSettingSymbol(symbol.symbol)]?.investment || '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch
                            checked={symbol.is_active}
                            onChange={() => handleToggleActive(symbol)}
                            size="small"
                          />
                          <Chip
                            label={symbol.is_active ? 'Hoạt động' : 'Tắt'}
                            color={symbol.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{symbol.slot_number}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Chỉnh sửa">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(symbol)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(symbol)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Container>

      {/* Dialog Thêm/Sửa Symbol */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSymbol ? 'Chỉnh Sửa Symbol' : 'Thêm Symbol Mới'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Tên Symbol"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
              placeholder="VD: BTCUSDT"
              fullWidth
              required
            />
            <TextField
              label="Thứ Tự Hiển Thị"
              type="number"
              value={formData.slot_number}
              onChange={(e) => setFormData({ ...formData, slot_number: parseInt(e.target.value, 10) || 0 })}
              fullWidth
              disabled
            />
            <FormControl fullWidth>
              <InputLabel>Webhook Version</InputLabel>
              <Select
                label="Webhook Version"
                value={formData.webhook_version}
                onChange={(e) => setFormData({ ...formData, webhook_version: e.target.value })}
              >
                <MenuItem value="v18">V18</MenuItem>
                <MenuItem value="v19">V19</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Investment USDT"
              type="number"
              value={formData.investment}
              onChange={(e) => setFormData({ ...formData, investment: e.target.value })}
              helperText="Để trống để dùng V17 Investment mặc định. Giá trị này áp dụng cho lệnh V18/V19 mở mới."
              inputProps={{ min: 0, step: 1 }}
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>Trạng thái:</Typography>
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <Typography color={formData.is_active ? 'success.main' : 'text.secondary'}>
                {formData.is_active ? 'Hoạt động' : 'Tắt'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Hủy</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Cập Nhật
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default SymbolManagement;
